import type { SSESessionSupersededEvent } from '@zyno-platform/shared';

import { type ComponentInternalInstance, getCurrentInstance, onBeforeUnmount, onMounted, onUnmounted } from 'vue';

import { GlobalState } from '@/global';
import { MobileBridge } from '@/modules/mobile/helper/bridge';

import { IdleTimer, useIdleTimeout } from '../helpers/idle.helpers';
import { Auth } from './auth.service';
import { Logger } from './logger';
import { SSEService } from './sse';

const IdleTimeoutMs = import.meta.env.VITE_APP_AUTH_TIMEOUT_MINS * 60 * 1000;
const IdleBlockingInstances = new Set<ComponentInternalInstance>();

const logger = Logger.createScoped('SessionManagement');

const SSEAuthPermissionsChannel = SSEService.createEventChannel('auth.permissions');
const SSEAuthLogoutChannel = SSEService.createEventChannel('auth.logout');
const SSEAuthSessionSupersededChannel = SSEService.createEventChannel('auth.session.superseded');

const SupersededCauseByEndpointType: Record<SSESessionSupersededEvent['endpointType'], string> = {
    browser: 'You were signed out because this account signed in from another browser.',
    workstation: 'You were signed out because this account signed in on another workstation.',
    device: 'You were signed out because this account signed in on another device.'
};

export class SessionManagement {
    static install() {
        this.useJwtRenewal();
        this.useSessionIdleTimeout();

        SSEAuthPermissionsChannel.subscribe(() => Auth.reloadPermissions(false));
        SSEAuthLogoutChannel.subscribe(() => Auth.clearBrowserSession());
        SSEAuthSessionSupersededChannel.subscribe(event => this.handleSessionSuperseded(event));
    }

    /**
     * A newer sign-in took this account's slot for some endpoint type. The event is published to the
     * *account*, so every live session receives it — including the new one that just claimed the slot.
     * Only the sessions named in `supersededSessionIds` may act on it; anyone else logging out here
     * would tear down the very session the login just established.
     *
     * Delivery is best-effort and this is purely a UX shortcut: the session row is already ended
     * server-side, so a superseded client that never sees this event is rejected on its next request
     * anyway. That makes "we can't tell which session we are" a safe no-op rather than a reason to guess.
     */
    private static handleSessionSuperseded(event: SSESessionSupersededEvent) {
        const sessionId = Auth.sessionId.value;
        if (!sessionId || !event.supersededSessionIds.includes(sessionId)) return;

        logger.info('Session superseded by a newer sign-in', { endpointType: event.endpointType });

        // Native owns the session inside the webview and never shows a web login, so ask it to tear
        // down rather than clearing the JWT out from under it.
        if (MobileBridge.isWithinZynoSuiteMobile) return void MobileBridge.logout();

        Auth.clearBrowserSession(SupersededCauseByEndpointType[event.endpointType]);
    }

    static useJwtRenewal() {
        let jwtRefreshInterval: number | null = null;

        const checkJwtRefresh = () => {
            if (this.hasSessionTimedOut()) return this.performTimeoutRedirect();
            const jwtExp = Auth.activeJwtExp;
            if (!jwtExp) return;

            const renewTs = jwtExp - 300_000; /* 5 minutes */
            if (Date.now() > renewTs) {
                Auth.renewJwt(jwtExp);
            }
        };

        function scheduleJwtRefresh() {
            jwtRefreshInterval = window.setInterval(checkJwtRefresh, 30_000);
            checkJwtRefresh();
        }

        function clearScheduledJwtRefresh() {
            if (jwtRefreshInterval) clearInterval(jwtRefreshInterval);
        }

        onMounted(scheduleJwtRefresh);
        onUnmounted(clearScheduledJwtRefresh);
    }

    static useSessionIdleTimeout() {
        IdleTimer.registerBrowserSessionScopeProvider(() =>
            !MobileBridge.isWithinZynoSuiteMobile && !GlobalState.isWorkstation && Auth.activeJwt ? Auth.browserSessionScope : null
        );
        useIdleTimeout({
            timeoutMs: 30_000, // this will fire more frequently than the timeout, so we dynamically configure our timeout
            onIdle: (idleMs: number) => {
                if (!GlobalState.user.value) return;

                // Inside the native webview the idle timeout is driven by the config
                // native hands us; on timeout we ask native to log out (it owns the session).
                if (MobileBridge.isWithinZynoSuiteMobile) return this.handleMobileIdle(idleMs);

                if (IdleBlockingInstances.size > 0) {
                    IdleTimer.keepSessionActive();
                    return;
                }

                const idleTimeoutMs = GlobalState.isWorkstation ? this.workstationIdleTimeoutMs : IdleTimeoutMs;
                if (idleTimeoutMs == null) return; // workstation with no configured idle timeout → never time out
                if (idleMs < idleTimeoutMs) return;
                if (!GlobalState.isWorkstation && Auth.rememberMeExpiresAt.value && Auth.rememberMeExpiresAt.value > Date.now()) return;
                this.performTimeoutRedirect();
            }
        });
        if (!MobileBridge.isWithinZynoSuiteMobile) {
            window.addEventListener('focus', () => this.checkSession());
        }
    }

    // The active workstation's configured idle timeout (ms), sourced from its login/renewal
    // response. null when no idle timeout is configured (i.e. the session never times out).
    private static get workstationIdleTimeoutMs(): number | null {
        const seconds = Auth.workstationIdleTimeoutSeconds;
        return seconds == null ? null : seconds * 1000;
    }

    private static hasFiredMobileLogout = false;
    private static handleMobileIdle(idleMs: number) {
        const idleTimeoutSeconds = MobileBridge.config.value?.idleTimeoutSeconds;
        if (idleTimeoutSeconds == null) return; // null (or config not yet loaded) → no idle timeout
        if (idleMs < idleTimeoutSeconds * 1000) return;
        if (this.hasFiredMobileLogout) return; // avoid re-firing while native tears down
        this.hasFiredMobileLogout = true;
        logger.info('Mobile session timed out due to inactivity');
        MobileBridge.logout();
    }

    static async checkSession() {
        if (!GlobalState.user.value) return;
        if (!Auth.activeJwt) return Auth.clearStoredJwt();
        if (!Auth.validateBrowserSession()) return;
        if (this.hasSessionTimedOut()) return this.performTimeoutRedirect();
        const jwtTimeout = Auth.activeJwtExp;
        // A suspended tab may wake with an expired token while another tab was in use.
        // Expiration is a renewal condition, not evidence of inactivity.
        if (!jwtTimeout || jwtTimeout <= Date.now()) await Auth.renewJwt(jwtTimeout);
    }

    private static hasSessionTimedOut() {
        if (MobileBridge.isWithinZynoSuiteMobile) return false;
        const timeoutMs = GlobalState.isWorkstation ? this.workstationIdleTimeoutMs : IdleTimeoutMs;
        if (timeoutMs == null || IdleTimer.getIdleMs() < timeoutMs) return false;
        if (IdleBlockingInstances.size > 0) return false;
        if (!GlobalState.isWorkstation && Auth.rememberMeExpiresAt.value && Auth.rememberMeExpiresAt.value > Date.now()) return false;
        return true;
    }

    static performTimeoutRedirect() {
        // Re-read shared activity at the destructive boundary, including focus/timer races.
        if (!this.hasSessionTimedOut()) return;
        logger.info('Session timed out');
        Auth.clearBrowserSession('Your session timed out due to inactivity.');
    }

    static useSessionTimeoutDisabler() {
        const instance = getCurrentInstance();
        if (!instance) return;

        onMounted(() => {
            IdleBlockingInstances.add(instance);
        });
        onBeforeUnmount(() => {
            IdleBlockingInstances.delete(instance);
        });
    }
}
